import React, { useState } from 'react';
import dayjs from 'dayjs';
import { CalendarBlankFilled, DeleteLined } from '@tag/tag-icons';

import Avatar from '@/components/common/Avatar/Avatar';
import { Pill } from '@/components/common/Pill/Pill';
import { DEFAULT_DATE_PATTERN, NOT_AVAILABLE_CHAR } from '@/config/common.config';
import { User } from '@/hooks/identity/responseModels/user';
import { FieldVerificationTypeEnum } from '@/graphql/contracts/_generated_/contracts';
import { FieldVerificationData } from './ContractIqTable';

import styles from './contract-iq-table.module.scss';

interface IContractIqRowProps {
  verification: FieldVerificationData;
  reasonCodeName: string;
  user: User | undefined;
  gridStyle?: React.CSSProperties;
  highlightedFields: string[];
  index: number;
  currentUserId: string | null;
  showRemovedAtColumn: boolean;
  onRemove: () => void;
}

const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000000';

const REASON_PILL_COLORS = {
  DEFAULT: '#6366f1',
  CODE_14: '#0d9488',
} as const;

const TYPE_PILL_COLORS = {
  [FieldVerificationTypeEnum.VERIFY]: '#0d9488',
  [FieldVerificationTypeEnum.UNVERIFY]: '#d97706',
  [FieldVerificationTypeEnum.REMOVED]: '#9ca3af',
} as const;

const TYPE_DISPLAY_LABELS: Record<FieldVerificationTypeEnum, string> = {
  [FieldVerificationTypeEnum.VERIFY]: 'Verify',
  [FieldVerificationTypeEnum.UNVERIFY]: 'Unverify',
  [FieldVerificationTypeEnum.REMOVED]: 'Removed',
};

function getHighlightClass(field: string, highlightedFields: string[]): string {
  if (highlightedFields.length === 0) return '';
  return highlightedFields.includes(field)
    ? styles['field-highlighted']
    : styles['field-dimmed'];
}

function FieldsCell({ fields, highlightedFields, isRemoved }: {
  fields: (string | null)[];
  highlightedFields: string[];
  isRemoved: boolean;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const filteredFields = fields.filter(Boolean) as string[];

  return (
    <div
      className={[
        styles['col-fields'],
        styles['fields-cell-wrapper'],
        isRemoved ? styles['fields-strikethrough'] : '',
      ].join(' ')}
      onMouseEnter={() => filteredFields.length > 0 && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className={styles['fields-list']}>
        {filteredFields.map((field, index, arr) => (
          <span
            key={field}
            className={`${styles['field-badge']} ${getHighlightClass(field, highlightedFields)}`}
          >
            {field}
            {index < arr.length - 1 && ',\u00A0'}
          </span>
        ))}
      </div>

      {showTooltip && filteredFields.length > 0 && (
        <div className={styles['fields-tooltip']}>
          {filteredFields.map((field) => (
            <div
              key={field}
              className={`${styles['fields-tooltip-item']} ${getHighlightClass(field, highlightedFields)}`}
            >
              {field}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function getRowClassName(hasActiveFilter: boolean, rowHasMatch: boolean, index: number, isRemoved: boolean): string {
  const base = `${styles.row} ${index % 2 === 0 ? styles['row-even'] : ''} ${isRemoved ? styles['row-removed'] : ''}`;
  if (!hasActiveFilter) return base;
  return `${base} ${rowHasMatch ? styles['row-highlighted'] : styles['row-dimmed']}`;
}

export default function ContractIqRow({
  verification,
  reasonCodeName,
  user,
  gridStyle,
  highlightedFields,
  index,
  currentUserId,
  showRemovedAtColumn,
  onRemove,
}: IContractIqRowProps) {
  const hasActiveFilter = highlightedFields.length > 0;
  const rowHasMatch = hasActiveFilter && verification.fields.some(
    (f) => f !== null && highlightedFields.includes(f)
  );
  const isSystemUser = verification.userID === SYSTEM_USER_ID;
  const userName = isSystemUser
    ? 'System'
    : user
      ? `${user.FirstName ?? ''} ${user.LastName ?? ''}`.trim() || 'unavailable'
      : 'unavailable';

  const reasonPillColor = verification.reasonCode === '14'
    ? REASON_PILL_COLORS.CODE_14
    : REASON_PILL_COLORS.DEFAULT;

  const isAutoUnverified = verification.type === FieldVerificationTypeEnum.UNVERIFY
    && verification.comment === 'Auto-unverified: field value changed';
  const isRemoved = verification.type === FieldVerificationTypeEnum.REMOVED;
  const typePillColor = TYPE_PILL_COLORS[verification.type] ?? TYPE_PILL_COLORS[FieldVerificationTypeEnum.VERIFY];

  return (
    <div
      className={[
        getRowClassName(hasActiveFilter, rowHasMatch, index, isRemoved),
        showRemovedAtColumn ? styles['has-removed-column'] : '',
      ].join(' ')}
      style={gridStyle}
    >
      <div className={styles['col-type']}>
        <Pill background={typePillColor}>
          {TYPE_DISPLAY_LABELS[verification.type] ?? verification.type}
        </Pill>
      </div>

      <FieldsCell fields={verification.fields} highlightedFields={highlightedFields} isRemoved={isRemoved} />

      <div className={styles['col-reason']}>
        <Pill background={reasonPillColor}>
          {reasonCodeName}
        </Pill>
      </div>

      <div className={styles['col-timestamp']}>
        <CalendarBlankFilled size="16" />
        <span>{dayjs(verification.verifiedAt).format(DEFAULT_DATE_PATTERN)}</span>
      </div>

      {showRemovedAtColumn && (
        <div className={styles['col-removed-at']}>
          {verification.updated ? (
            <>
              <CalendarBlankFilled size="16" />
              <span>{dayjs(verification.updated).format(DEFAULT_DATE_PATTERN)}</span>
            </>
          ) : NOT_AVAILABLE_CHAR}
        </div>
      )}

      <div className={styles['col-user']}>
        <Avatar user={isSystemUser ? null : user ?? null} size={28} isLoading={false} />
        <span>{userName}</span>
      </div>

      <div className={styles['col-notes']}>
        {verification.comment || NOT_AVAILABLE_CHAR}
      </div>

      <div className={styles['col-actions']}>
        {!isAutoUnverified && !isRemoved && currentUserId !== null && verification.userID === currentUserId && (
          <button
            type="button"
            className={`${styles['action-btn']} ${styles['action-btn-remove']}`}
            onClick={onRemove}
            title="Remove verification"
          >
            <DeleteLined size="14" />
          </button>
        )}
      </div>
    </div>
  );
}
